import axios from "axios";
import {
  reconcileCartWithLiveStock,
  getCheckoutReadyItems,
} from "./cartStock";
import { getProductStockAvailable } from "./productSizes";
import { parseStockErrorMessage } from "./cartStockMessages";

const normalizeProductId = (item) => {
  const id = item?._id ?? item?.productId;
  if (!id) return null;
  return String(id);
};

const buildStockResult = (items, stockUpdates, issues) => {
  const reconciliation = reconcileCartWithLiveStock(items, stockUpdates, issues);
  const readyItems = getCheckoutReadyItems(reconciliation.items);

  return {
    valid:
      issues.length === 0 &&
      readyItems.length === items.length &&
      !reconciliation.hadChanges,
    ...reconciliation,
    items: readyItems,
    issues,
  };
};

const fetchProductsIndividually = async (items) => {
  const stockUpdates = await Promise.all(
    items.map(async (item) => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/product/products/${item.productId}`,
          { timeout: 12000 }
        );
        const product = response.data?.product || response.data;
        return {
          productId: item.productId,
          name: product?.name || item.name,
          available: getProductStockAvailable(product, item.selectedSize || null),
        };
      } catch {
        const fallbackAvailable =
          item.stockAvailable != null
            ? Math.max(0, parseInt(item.stockAvailable, 10) || 0)
            : 0;
        return {
          productId: item.productId,
          name: item.name,
          available: fallbackAvailable,
        };
      }
    })
  );

  const issues = items
    .map((item) => {
      const live = stockUpdates.find(
        (entry) => String(entry.productId) === String(item.productId)
      );
      const available = live?.available ?? 0;
      const requested = Math.max(1, parseInt(item.quantity, 10) || 1);

      if (available < requested) {
        return {
          productId: item.productId,
          name: item.name,
          requested,
          available,
          status: available === 0 ? "out_of_stock" : "insufficient",
        };
      }
      return null;
    })
    .filter(Boolean);

  return { stockUpdates, issues };
};

export const fetchCartStockValidation = async (cartItems) => {
  const items = cartItems
    .map((item) => ({
      productId: normalizeProductId(item),
      name: item.name,
      quantity: item.quantity,
      selectedSize: item.selectedSize || undefined,
      stockAvailable: item.stockAvailable,
    }))
    .filter((item) => item.productId);

  if (items.length === 0) {
    return { valid: true, issues: [], stockUpdates: [] };
  }

  try {
    const response = await axios.post(
      `${process.env.REACT_APP_SERVER_URL}/product/validate-cart-stock`,
      { items },
      { timeout: 15000 }
    );
    return response.data;
  } catch (primaryError) {
    try {
      const { stockUpdates, issues } = await fetchProductsIndividually(items);
      return {
        valid: issues.length === 0,
        issues,
        stockUpdates,
      };
    } catch {
      throw primaryError;
    }
  }
};

export const validateAndReconcileCart = async (cartItems) => {
  const { issues, stockUpdates } = await fetchCartStockValidation(cartItems);
  return buildStockResult(cartItems, stockUpdates, issues);
};

export const refreshCartStockLevels = async (cartItems, replaceCartItems) => {
  if (!cartItems?.length) {
    return { ok: true, items: [], messages: [], hadChanges: false, issues: [] };
  }

  const result = await validateAndReconcileCart(cartItems);

  if (result.hadChanges || result.items.length !== cartItems.length) {
    replaceCartItems(result.items);
  }

  return {
    ok: result.items.length > 0,
    ...result,
  };
};

/** When server sync/checkout rejects stock, refresh cart and return a clear message. */
export const recoverFromStockError = async (error, cartItems, replaceCartItems) => {
  const raw = error?.response?.data?.message || "";
  let refreshResult = null;

  try {
    refreshResult = await refreshCartStockLevels(cartItems, replaceCartItems);
  } catch {
    refreshResult = null;
  }

  const friendly =
    parseStockErrorMessage(raw) ||
    (refreshResult?.messages?.length
      ? refreshResult.messages.join(" ")
      : "Some items in your cart are no longer available. Please review your cart and try again.");

  return { message: friendly, refreshResult };
};
